/* eslint-disable no-unused-vars */

import { Box, Button, FormHelperText, TextField, Theme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import { FormikProps } from 'formik';
import { get } from 'lodash';
import React, { ReactNode, useCallback } from 'react';
import { useRecoilValue, useRecoilValueLoadable } from 'recoil';
import { themeModeAtom, themeModeEnum } from 'src/recoil/atoms/themeModeAtom';
import { LabelledOutline } from '../../../common/final-form-material-ui';
import { FormikTextField } from '../../../common/formik-material-ui';
import { FormikSelect } from '../../../common/formik-material-ui/FormikSelect';
import { pipeIsUrl } from '../../../common/validators';
import { user } from '../../../recoil/atoms/user';
import { WebRTCRecorder } from './WebRTCRecorder';

/**
 * Create style for page
 * NOTE: do not use @mui/styles since theme.spacing is not found
 * @see https://stackoverflow.com/questions/45909258/property-theme-does-not-exist-when-using-typescript
 */
const useStyles = makeStyles((theme: Theme) => ({
  field: {
    marginTop: theme.spacing(1), // space between fields
    marginBottom: theme.spacing(1.5), // space between fields
    '&:last-of-type': {
      marginBottom: '0 !important',
    },
    // '& .MuiFormLabel-root[data-shrink=false]': {
    //   lineHeight: 0.25, // adjust label position after field height
    // },
    // '& .MuiInputBase-input': {
    //   padding: '14px 12px', // shrink field height
    // },
    // TODO: move to theme
    '& .MuiFormHelperText-root': {
      marginLeft: 0, // adjust helper text
    },
    '& .MuiInputLabel-root[data-shrink=false]': {
      opacity: 0.5, // make placeholder lighter
    },
  },
  fileUploadLabel: {
    marginTop: theme.spacing(1),
    marginRight: theme.spacing(1),
    height: 'min-content',
  },
  button: {
    marginTop: theme.spacing(1),
    marginRight: theme.spacing(1),
    marginBottom: theme.spacing(1),
    '& .MuiButton-label': {
      padding: 2,
    },
  },
  audioContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  audio: {
    transition: 'all 0.5s linear',
    boxShadow: '0px 0px 0px 0px',
    backgroundColor: '#f0f3f4',
    borderRadius: '7px 7px 7px 7px',
    height: 32,
    maxWidth: '-webkit-fill-available',
    margin: '0px auto',
    '&:focus': {
      outline: 0,
    },
  },
  labelledOutline: {
    // marginTop: theme.spacing(1),
  },
}));

interface IRecordNow {
  className?: string;
  dropdownLabel: string;
  dropdownName: string;
  dropdownType: 'mp3' | 'tts' | 'record';
  filenameLabel?: string;
  filenameName: string;
  filesizeName?: string;
  ttsLabel?: string;
  ttsName?: string;
  outlineLabel: string;
  filename: string;
  filepath: string;
  formik: FormikProps<any>;
  onChangeFile: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onRecording: (files: FileList | [File]) => void;
  children: ReactNode;
  fileSizeError?: boolean;
  setVmDropRecording?: React.Dispatch<React.SetStateAction<boolean>>;
  helperText?: string;
}

/**
 * Record now component responsible for text-to-speach, mp3 upload and recording
 *
 * @param param props
 * @param param props.dropdownLabel Label to add to drop-down
 * @param param props.dropdownName Name of Formik field that contains value
 * @param param props.dropdownType Type to show user interface
 * @param param props.filenameLabel Label to add to textfield
 * @param param props.filenameName Name of Formik field that contains value
 * @param param props.ttsLabel Label to add to textfield
 * @param param props.ttsName Name of Formik field that contains value
 * @param param props.outlineLabel Label to add to outline
 * @param param props.filename Filename to show user
 * @param param props.filepath Path to audio source
 * @param param props.formik Formik form object
 * @param param props.onChangeFile Callback for file change
 * @param param props.onRecording Callback for uploaded recording
 * @param param props.children JSX of MenuItem to show dropdown
 *
 * @example
 *  <RecordNow
 *    dropdownLabel="Queue Greeting Type"
 *    dropdownName="submitQueueEditPayload.greetingType"
 *    dropdownType={
 *      formik.values.submitQueueEditPayload?.greetingType
 *    }
 *    outlineLabel="Queue Greeting"
 *    ttsLabel="Queue Greeting"
 *    ttsName="submitQueueEditPayload.greeting"
 *    filenameName="submitQueueGreetingMp3File.files"
 *    formik={formik}
 *    filename={filenames.greeting}
 *    filepath={filepaths.greeting}
 *    onChangeFile={onChangeFile('greeting')}
 *    onRecording={handleRecording('greeting')}
 *  >
 *    <MenuItem value="none">
 *      <em>None</em>
 *    </MenuItem>
 *    <MenuItem value="tts">Text-to-Speech</MenuItem>
 *    <MenuItem value="mp3">MP3</MenuItem>
 *    <MenuItem value="record">Record Now</MenuItem>
 *  </RecordNow>
 */
export const RecordNow = ({
  className,
  dropdownLabel,
  dropdownName,
  dropdownType,
  filenameLabel = 'Filename',
  filenameName,
  filesizeName,
  ttsLabel,
  ttsName,
  outlineLabel,
  filename,
  filepath,
  formik,
  onChangeFile,
  onRecording,
  children,
  fileSizeError,
  setVmDropRecording,
  helperText,
}: IRecordNow) => {
  const classes = useStyles();

  const handleOnChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (ttsName) {
        formik.setFieldValue(ttsName, event.target.value);
        formik.setFieldTouched(
          ttsName,
          get(formik.initialValues, ttsName) !== event.target.value
        );
      }
    },
    [ttsName, formik]
  );
  const userAtom = useRecoilValueLoadable(user);
  const loginUser = userAtom.contents?.user ?? {};
  const themeMode = useRecoilValue(themeModeAtom);

  return (
    <LabelledOutline
      label={outlineLabel}
      className={clsx(classes.labelledOutline, className)}
      backgroundColor={
        themeMode === themeModeEnum.DARK_MODE ? '#121c21' : '#fff'
      }
    >
      <FormikSelect
        name={dropdownName}
        className={classes.field}
        style={{ marginBottom: 0 }}
        formik={formik}
        // defaultValue={queue.voicemailType}
        fullWidth
        label={dropdownLabel}
        size="small"
        variant="outlined"
        onChange={() => {
          setVmDropRecording?.((old) => !old);
        }}
      >
        {children}
      </FormikSelect>
      <fieldset className={classes.audioContainer}>
        {dropdownType === 'mp3' && (
          <Box display="flex">
            <label htmlFor={`${filenameName}.id`}>
              <Button
                data-qa="upload-hold-music-button"
                component="span"
                variant="contained"
                color="primary"
                className={classes.button}
                disableElevation
              >
                Upload
              </Button>
            </label>
            <FormikTextField
              data-qa="upload-hold-music-mp3-filename"
              name={filesizeName ?? 'submitHoldMusicMp3File.size'}
              className={`flex-grow-1 ${classes.field}`}
              formik={formik}
              disabled={true}
              label={filenameLabel}
              // value={filename}
              variant="outlined"
              size="small"
              required
              // error={fileSizeError}
              // helperText={
              //   fileSizeError &&
              //   'File is too large! Please select something less than 2MB in size'
              // }
            />
          </Box>
        )}
        <input
          id={`${filenameName}.id`}
          name={filenameName}
          type="file"
          accept="audio/mp3"
          hidden={true}
          multiple={false}
          onChange={(event) => {
            formik.setFieldValue(filenameName, event.target.files);
            if (event.target.files && filesizeName) {
              const files = event.target.files;
              const filezie = files?.[0]?.size;
              const filename =
                (files?.length ?? 0) > 1
                  ? `${files?.length} files | size:${filezie}`
                  : `${files?.[0]?.name || 'Unknown'} | size:${filezie}`;
              formik.setFieldValue(filesizeName, filename);
            }

            onChangeFile(event);
          }}
        />
        {
          // hide if not mp3 or filepath empty
          dropdownType === 'mp3' && filepath && (
            <audio controls src={filepath} className={classes.audio}>
              Your browser does not support the audio element.
            </audio>
          )
        }
      </fieldset>
      {dropdownType === 'tts' && (
        <TextField
          label={ttsLabel}
          value={pipeIsUrl(true)(get(formik.values, ttsName ?? ''))}
          onChange={handleOnChange}
          variant="outlined"
          multiline={true}
          fullWidth
        />
      )}
      {dropdownType === 'record' && (
        <WebRTCRecorder
          className={classes.audioContainer}
          setFileToUpload={(file: File) => {
            onRecording([file]);
          }}
        />
      )}
      <FormHelperText style={{ marginTop: 3 }}>{helperText}</FormHelperText>
    </LabelledOutline>
  );
};
